/**
 * @file resume_analytics.ts
 *
 * @fileoverview Helper functions for resume analytics events.
 */

import {
  DIMENSIONS,
  EventParameters,
  setEventParams,
} from '~/jobiqo/lib/analytics';
import { ANONYMOUS_USER } from '~/jobiqo/lib/auth';
import { User } from '~/jobiqo/models/user';
import { Resume } from '~/jobiqo/types/types';
import { concatArrayItems } from '~/jobiqo/utils/mapper';

interface ResumeAnalyticsDimensionsOptions {
  /**
   * Whether to include the visible/hidden resume state dimension.
   */
  includeState?: boolean;
}

const getResumeSkills = (resume: Resume): string =>
  resume.skills
    ?.map((skill) => skill?.term?.label ?? skill?.label)
    .filter(Boolean)
    .join(' | ') ?? '';

/**
 * Returns shared dimensions for recruiter-facing resume analytics events.
 *
 * @param {Resume} resume
 *   The resume to return dimensions for.
 * @param {User | undefined} user
 *   Current user.
 * @param {ResumeAnalyticsDimensionsOptions} options
 *   Additional dimension options.
 *
 * @returns {EventParameters}
 *   The event parameters for this resume.
 */
export const getResumeAnalyticsDimensions = (
  resume: Resume,
  user?: User,
  options: ResumeAnalyticsDimensionsOptions = {},
): EventParameters => {
  return setEventParams({
    [DIMENSIONS.entity_id]: resume.id,
    [DIMENSIONS.title]: resume.title,
    [DIMENSIONS.skills]: getResumeSkills(resume),
    [DIMENSIONS.current_user_id]: user?.id,
    [DIMENSIONS.current_user_role]:
      concatArrayItems(user?.roles, 'targetId') ?? ANONYMOUS_USER,
    [DIMENSIONS.organization_id]:
      user?.activeOrganization?.id ?? user?.organization?.id,
    [DIMENSIONS.organization_name]:
      user?.activeOrganization?.name ?? user?.organization?.name,
    ...(options.includeState
      ? {
          [DIMENSIONS.state]: resume.privateFieldsAccess ? 'visible' : 'hidden',
        }
      : {}),
  });
};
